import {
  SUPPORT_DEPARTMENT,
  SUPPORT_NEW_SYSTEM_LABEL,
  SUPPORT_PORTAL_URL,
  SUPPORT_REASONS,
  SUPPORT_STEP_TITLE,
  SUPPORT_STEPS,
  SUPPORT_SYSTEM_NAME,
  SUPPORT_TITLE_MAX,
  firstInvalidSupportStep,
  supportReasonLabel,
  validateSupportStep,
  type SupportForm,
  type SupportStep,
} from '@supply/contracts'
import { AlertTriangle, ArrowLeft, Check, Copy, ExternalLink, FileText, Loader2 } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'

import { ErrorState } from '@/components/states'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { openSupportTicket, supportFormOptions, uploadSupportAttachments, type SupportFormInfo } from '@/data/api'
import { fetchBridgeSession } from '@/lib/bridge-auth'
import { appEnv } from '@/lib/env'
import { useOptionalSession } from '@/lib/session'
import { getSupabase } from '@/lib/supabase'
import { useWrite } from '@/lib/use-write'
import { cn } from '@/lib/utils'

import { AttachmentPicker, isPdf } from './AttachmentPicker'

/**
 * "Abrir um chamado sobre o sistema": o formulário do Portal de Chamados
 * (pc.flexibase.com.br → Abrir chamado → Sistemas → "Chamado para Sistemas"),
 * dentro de um diálogo. Departamento, tipo e sistema já vêm escolhidos (SUPPLY),
 * então as etapas são: solicitante → motivo → assunto e detalhes → revisar.
 * Mesmos campos, textos e validações do PC (`@supply/contracts`
 * `support-ticket.ts` é o espelho); os motivos e o rótulo dos detalhes vêm do
 * template vivo do PC. O envio é `openSupportTicket` em `data/api.ts`.
 */

const REMEMBER_KEY = 'supply.chamado.ramal'
/**
 * Anexo depende da rota `/chamados/:id/anexos` do PC, que ainda não existe
 * (PR Flexibase-Projects/PC#171 trouxe só formulário e abertura). Desligado,
 * o campo some e `prefill.files` é ignorado — ligue quando a rota entrar.
 */
const SUPPORT_ATTACHMENTS = false
const MOTIVO_FIELD = 'hub_motivo'
const DETAILS_FIELD = 'hub_descricao'

/** Ramal fica só neste navegador, para o próximo chamado. */
function loadExtension(): string {
  try {
    return (window.localStorage.getItem(REMEMBER_KEY) ?? '').replace(/\D/g, '')
  } catch {
    return ''
  }
}

function saveExtension(value: string): void {
  try {
    window.localStorage.setItem(REMEMBER_KEY, value)
  } catch {
    // sem armazenamento: segue sem lembrar
  }
}

/** Nome e e-mail do login (bridge ou sessão local), sem depender do `SessionProvider`. */
async function ssoRequester(): Promise<{ name: string; email: string } | null> {
  try {
    if (appEnv.authMode === 'local') {
      const user = (await getSupabase().auth.getSession()).data.session?.user
      if (!user) return null
      const meta = user.user_metadata as { full_name?: string; name?: string } | undefined
      return { name: meta?.full_name ?? meta?.name ?? '', email: user.email ?? '' }
    }
    const session = await fetchBridgeSession()
    return session ? { name: session.displayName === session.email ? '' : session.displayName, email: session.email } : null
  } catch {
    return null
  }
}

type Options = { kind: 'carregando' } | { kind: 'erro'; reason: string } | { kind: 'ok'; info: SupportFormInfo }

/**
 * Chamado já começado — a tela de erro manda motivo "erro", assunto,
 * detalhes técnicos e o PDF com stack e buffer.
 */
export interface SupportPrefill {
  readonly reason: string
  readonly title: string
  readonly description: string
  readonly files?: readonly File[]
}

export function SupportTicketDialog({
  open,
  onOpenChange,
  prefill,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  prefill?: SupportPrefill | undefined
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92dvh] max-w-lg flex-col gap-0 overflow-hidden p-0">
        {/* Remontar a cada abertura começa um chamado do zero. */}
        {open && <SupportFunnel onClose={() => onOpenChange(false)} prefill={prefill} />}
      </DialogContent>
    </Dialog>
  )
}

function SupportFunnel({ onClose, prefill }: { onClose: () => void; prefill?: SupportPrefill | undefined }) {
  // Aberto pela tela de erro, a sessão pode não existir (ou ter quebrado):
  // sem ela o nome vem do cadastro do PC, ou fica vazio e o funil pede.
  const member = useOptionalSession()?.member
  const { run, pending } = useWrite()

  const [options, setOptions] = React.useState<Options>({ kind: 'carregando' })
  const [attempt, setAttempt] = React.useState(0)

  const [step, setStep] = React.useState<SupportStep>('voce')
  const [attempted, setAttempted] = React.useState(false)
  const [editingFromReview, setEditingFromReview] = React.useState(false)

  const [name, setName] = React.useState(member?.full_name ?? '')
  const [email, setEmail] = React.useState(member?.email ?? '')
  const [extension, setExtension] = React.useState(loadExtension)
  const [reason, setReason] = React.useState(prefill?.reason ? supportReasonLabel(prefill.reason) : '')
  const [title, setTitle] = React.useState((prefill?.title ?? '').slice(0, SUPPORT_TITLE_MAX))
  const [description, setDescription] = React.useState(prefill?.description ?? '')
  const [files, setFiles] = React.useState<File[]>(() => (SUPPORT_ATTACHMENTS ? [...(prefill?.files ?? [])] : []))
  const [error, setError] = React.useState<string | null>(null)
  const [done, setDone] = React.useState<{ protocol: string; url: string; failures: number } | null>(null)

  const heading = React.useRef<HTMLHeadingElement>(null)
  const body = React.useRef<HTMLDivElement>(null)
  const firstRender = React.useRef(true)

  React.useEffect(() => {
    let cancelled = false
    setOptions({ kind: 'carregando' })
    // Fora da sessão (tela de erro de tela cheia), quem está logado vem do SSO —
    // antes do formulário, porque ler a sessão também guarda o token do bridge.
    void (member ? Promise.resolve(null) : ssoRequester())
      .then(async (sso) => [await supportFormOptions(), sso] as const)
      .then(([r, sso]) => {
        if (cancelled) return
        if (sso?.name) setName((current) => current || sso.name)
        if (sso?.email) setEmail((current) => current || sso.email)
        if (!r.ok) return setOptions({ kind: 'erro', reason: r.reason })
        setOptions({ kind: 'ok', info: r.value })
        // O cadastro do PC completa o que a sessão do Supply não tem.
        const who = r.value.solicitante
        if (who?.nome) setName((current) => current || who.nome || '')
        if (who?.email) setEmail(who.email)
        if (who?.ramal) setExtension((current) => current || (who.ramal ?? '').replace(/\D/g, ''))
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a sessão só completa a primeira carga
  }, [attempt])

  const fields = options.kind === 'ok' ? options.info.template.fields : []
  const motivoField = fields.find((f) => f.id === MOTIVO_FIELD)
  const reasons: readonly string[] = motivoField?.options?.length
    ? motivoField.options
    : SUPPORT_REASONS.map((r) => r.label)
  const detailsLabel = fields.find((f) => f.id === DETAILS_FIELD)?.label || 'Detalhes'
  const motivoTitle = motivoField?.label || SUPPORT_STEP_TITLE.motivo
  const newSystem = reason === SUPPORT_NEW_SYSTEM_LABEL

  const form: SupportForm = { name, email, extension, reason, title, description }
  const index = SUPPORT_STEPS.indexOf(step)
  const errors = validateSupportStep(step, form, detailsLabel)
  const shown = attempted ? errors : {}

  // Pré-preenchido com o solicitante já completo: pula direto para os
  // detalhes, onde a pessoa conta o que fazia. Senão, começa no solicitante.
  const skipped = React.useRef(false)
  React.useEffect(() => {
    if (!prefill || skipped.current || options.kind !== 'ok') return
    skipped.current = true
    if (Object.keys(validateSupportStep('voce', form)).length === 0 && reason) setStep('detalhes')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só na primeira carga das opções
  }, [options.kind])

  // Etapa nova: topo do diálogo e foco no título (leitor de tela anuncia).
  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    body.current?.scrollTo({ top: 0 })
    heading.current?.focus()
  }, [step, done])

  function show(next: SupportStep) {
    setStep(next)
    setAttempted(false)
  }

  function next() {
    setAttempted(true)
    if (Object.keys(validateSupportStep(step, form, detailsLabel)).length > 0) {
      // Leva o foco ao primeiro campo com problema.
      window.requestAnimationFrame(() => body.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus())
      return
    }
    if (editingFromReview) {
      setEditingFromReview(false)
      return show('revisar')
    }
    const following = SUPPORT_STEPS[index + 1]
    if (following) show(following)
  }

  function back() {
    const previous = SUPPORT_STEPS[index - 1]
    if (previous) show(previous)
  }

  function edit(target: SupportStep) {
    setEditingFromReview(true)
    show(target)
  }

  async function submit() {
    setError(null)
    const firstBad = firstInvalidSupportStep(form)
    if (firstBad) return edit(firstBad)
    if (pending || options.kind !== 'ok') return
    const r = await run(
      openSupportTicket({
        nome: name.trim(),
        ramal: extension.trim(),
        assunto: title.trim(),
        respostas: { [MOTIVO_FIELD]: reason, [DETAILS_FIELD]: description.trim() },
      }),
      { error: 'Não foi possível abrir o chamado' },
    )
    if (!r.ok) return setError(r.reason)
    saveExtension(extension.trim())
    const failures = files.length ? await uploadSupportAttachments(r.value.id, files) : 0
    toast.success(`Chamado ${r.value.protocolo} enviado`, {
      description: `${SUPPORT_DEPARTMENT} já recebeu o seu pedido.`,
    })
    setDone({
      protocol: r.value.protocolo,
      url: r.value.url || `${SUPPORT_PORTAL_URL}/chamados/${encodeURIComponent(r.value.id)}`,
      failures,
    })
  }

  if (done) return <Created ref={heading} {...done} subject={title.trim()} onClose={onClose} />

  const sending = pending

  return (
    <>
      <DialogHeader className="space-y-3 border-b border-border px-5 pb-4 pt-5 text-left sm:px-6">
        <div className="flex items-center gap-2 pr-8 text-[13px] text-muted-foreground">
          <span>
            Para <span className="font-medium text-foreground">{SUPPORT_DEPARTMENT}</span> · {SUPPORT_SYSTEM_NAME}
          </span>
          <span className="ml-auto font-mono tabular-nums">
            Etapa {index + 1} de {SUPPORT_STEPS.length}
          </span>
        </div>
        <div
          role="progressbar"
          aria-label="Progresso do chamado"
          aria-valuemin={1}
          aria-valuemax={SUPPORT_STEPS.length}
          aria-valuenow={index + 1}
          className="h-1.5 overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ width: `${((index + 1) / SUPPORT_STEPS.length) * 100}%` }}
          />
        </div>
        <DialogTitle ref={heading} tabIndex={-1} className="text-lg leading-tight outline-none">
          {step === 'motivo' ? motivoTitle : SUPPORT_STEP_TITLE[step]}
        </DialogTitle>
        <DialogDescription>
          {step === 'voce'
            ? 'Quem precisa do atendimento. Já vem com os seus dados.'
            : step === 'detalhes'
              ? (options.kind === 'ok' && options.info.template.descricao) ||
                'Conte o que aconteceu ou o que você precisa.'
              : `Chamado no Portal de Chamados · ${name.trim().split(' ')[0] ?? ''}`}
        </DialogDescription>
      </DialogHeader>

      <div ref={body} className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
        {options.kind === 'carregando' && (
          <div role="status" aria-live="polite" className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Carregando formulário
          </div>
        )}

        {options.kind === 'erro' && (
          <div className="space-y-3">
            <ErrorState
              title="Não deu para abrir o formulário agora"
              description={options.reason}
              onRetry={() => setAttempt((n) => n + 1)}
            />
            {/* Saída que sempre funciona: o mesmo chamado direto no Portal. */}
            <p className="text-center text-[13px] text-muted-foreground">
              Ou abra direto no{' '}
              <a
                href={`${SUPPORT_PORTAL_URL}/criar-chamado`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Portal de Chamados
                <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
            </p>
          </div>
        )}

        {options.kind === 'ok' && step === 'voce' && (
          <div className="space-y-5">
            <Field label="Nome" htmlFor="ch-nome" error={shown.name} required>
              <Input
                id="ch-nome"
                autoComplete="name"
                value={name}
                maxLength={120}
                onChange={(e) => setName(e.target.value)}
                aria-invalid={Boolean(shown.name)}
              />
            </Field>
            <Field
              label="E-mail"
              htmlFor="ch-email"
              hint="O do seu login. É por ele que o chamado aparece em Chamados, no Portal."
            >
              <Input id="ch-email" type="email" value={email} readOnly className="bg-muted/40 text-muted-foreground" />
            </Field>
            <Field label="Ramal" htmlFor="ch-ramal" error={shown.extension}>
              <Input
                id="ch-ramal"
                className="w-32 font-mono tabular-nums"
                inputMode="numeric"
                autoComplete="tel-extension"
                value={extension}
                maxLength={10}
                onChange={(e) => setExtension(e.target.value.replace(/\D/g, ''))}
                aria-invalid={Boolean(shown.extension)}
              />
            </Field>
          </div>
        )}

        {options.kind === 'ok' && step === 'motivo' && (
          <div role="radiogroup" aria-label={motivoTitle} className="grid gap-2">
            {reasons.map((label) => (
              <Choice
                key={label}
                selected={reason === label}
                onSelect={() => {
                  setReason(label)
                  if (editingFromReview) {
                    setEditingFromReview(false)
                    show('revisar')
                  } else show('detalhes')
                }}
                title={label}
              />
            ))}
            {shown.reason && <FieldError>{shown.reason}</FieldError>}
          </div>
        )}

        {options.kind === 'ok' && step === 'detalhes' && (
          <div className="space-y-5">
            <Field label="Assunto do chamado" htmlFor="ch-assunto" error={shown.title} required>
              <Input
                id="ch-assunto"
                value={title}
                maxLength={SUPPORT_TITLE_MAX}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Resumo em poucas palavras"
                aria-invalid={Boolean(shown.title)}
              />
            </Field>
            <Field label="Sistema" htmlFor="ch-sistema">
              <Input
                id="ch-sistema"
                value={newSystem ? '— (pedido de sistema novo)' : options.info.sistema}
                readOnly
                className="bg-muted/40 text-muted-foreground"
              />
            </Field>
            <Field label={detailsLabel} htmlFor="ch-detalhes" error={shown.description} required>
              <Textarea
                id="ch-detalhes"
                rows={5}
                className="min-h-[120px] resize-y leading-relaxed"
                value={description}
                maxLength={4000}
                onChange={(e) => setDescription(e.target.value)}
                aria-invalid={Boolean(shown.description)}
              />
            </Field>
            {SUPPORT_ATTACHMENTS && (
              <Field
                label="Foto, print ou PDF"
                htmlFor="ch-anexos"
                optional
                hint="A imagem mostra exatamente o que você viu. Dá para colar um print com Ctrl+V."
              >
                <AttachmentPicker id="ch-anexos" files={files} onChange={setFiles} />
              </Field>
            )}
          </div>
        )}

        {options.kind === 'ok' && step === 'revisar' && (
          <div className="space-y-4">
            <dl className="divide-y divide-border rounded-md border border-border">
              <ReviewRow label="Para">
                {SUPPORT_DEPARTMENT} · {options.info.template.nome}
              </ReviewRow>
              <ReviewRow label="Solicitante" onEdit={() => edit('voce')}>
                {name.trim()}
                <span className="block font-mono text-[13px] tabular-nums text-muted-foreground">
                  {email}
                  {extension ? ` · ramal ${extension}` : ''}
                </span>
              </ReviewRow>
              <ReviewRow label="Motivo" onEdit={() => edit('motivo')}>
                {reason}
              </ReviewRow>
              <ReviewRow label="Sistema">{newSystem ? '—' : options.info.sistema}</ReviewRow>
              <ReviewRow label="Assunto e detalhes" onEdit={() => edit('detalhes')}>
                <span className="font-medium">{title.trim()}</span>
                <span className="mt-0.5 line-clamp-3 block whitespace-pre-line text-[13px] text-muted-foreground">
                  {description.trim()}
                </span>
                {files.length > 0 && (
                  <span className="mt-1 flex items-center gap-1.5 text-[13px] text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" aria-hidden />
                    {files.length === 1 ? '1 anexo' : `${files.length} anexos`}
                    {files.some(isPdf) ? ' (com PDF)' : ''}
                  </span>
                )}
              </ReviewRow>
            </dl>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>{error}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {options.kind === 'ok' && (
        <DialogFooter className="flex-row items-center justify-between gap-2 border-t border-border px-5 py-3 sm:justify-between sm:space-x-0 sm:px-6">
          {index > 0 ? (
            <Button variant="ghost" onClick={back} disabled={sending}>
              <ArrowLeft aria-hidden />
              Voltar
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
          )}
          <div className="ml-auto">
            {step === 'revisar' ? (
              <Button onClick={() => void submit()} disabled={sending}>
                {sending && <Loader2 className="animate-spin" aria-hidden />}
                {sending ? (files.length ? 'Enviando o chamado e os anexos…' : 'Enviando…') : 'Enviar chamado'}
              </Button>
            ) : step === 'motivo' && !reason ? null : (
              <Button onClick={next}>{editingFromReview ? 'Voltar para a revisão' : 'Continuar'}</Button>
            )}
          </div>
        </DialogFooter>
      )}
    </>
  )
}

function Field({
  label,
  htmlFor,
  error,
  hint,
  optional,
  required,
  children,
}: {
  label: string
  htmlFor: string
  error?: string | undefined
  hint?: React.ReactNode
  optional?: boolean
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>
        {label}
        {required && (
          <span className="ml-0.5 text-destructive" aria-hidden>
            *
          </span>
        )}
        {optional && <span className="ml-1 font-normal text-muted-foreground">(opcional)</span>}
      </Label>
      {children}
      {error ? <FieldError>{error}</FieldError> : hint ? <p className="text-[12px] text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" className="text-[13px] text-destructive">
      {children}
    </p>
  )
}

function ReviewRow({ label, onEdit, children }: { label: string; onEdit?: () => void; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <dt className="text-[12px] text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 break-words text-sm">{children}</dd>
      </div>
      {onEdit && (
        <Button
          variant="ghost"
          size="sm"
          className="-mr-2 shrink-0 text-primary"
          onClick={onEdit}
          aria-label={`Alterar ${label.toLowerCase()}`}
        >
          Alterar
        </Button>
      )}
    </div>
  )
}

function Choice({ selected, onSelect, title }: { selected: boolean; onSelect: () => void; title: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        'flex min-h-12 w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent',
      )}
    >
      <span
        className={cn(
          'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40',
        )}
        aria-hidden
      >
        {selected && <Check className="h-3 w-3" />}
      </span>
      <span className="min-w-0 flex-1 text-sm font-medium">{title}</span>
    </button>
  )
}

const Created = React.forwardRef<
  HTMLHeadingElement,
  { protocol: string; url: string; failures: number; subject: string; onClose: () => void }
>(function Created({ protocol, url, failures, subject, onClose }, ref) {
  return (
    <>
      <DialogHeader className="space-y-1 px-5 pb-2 pt-6 text-left sm:px-6">
        <DialogTitle ref={ref} tabIndex={-1} className="text-lg leading-tight outline-none">
          Chamado enviado
        </DialogTitle>
        <DialogDescription>
          {SUPPORT_DEPARTMENT} já recebeu o seu pedido. Você acompanha o andamento em Chamados, no Portal de Chamados.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 px-5 py-4 sm:px-6">
        {failures > 0 && (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {failures === 1 ? 'Um anexo não foi enviado.' : `${failures} anexos não foram enviados.`} Você pode mandar de
            novo pelo chamado no Portal.
          </div>
        )}
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-md border border-border p-3 text-sm">
          <dt className="text-muted-foreground">Protocolo</dt>
          <dd className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-mono font-medium tabular-nums">{protocol}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="h-7 w-7"
              aria-label="Copiar protocolo"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(protocol)
                  .then(() => toast.success('Protocolo copiado.'))
                  .catch(() => toast.error('Não foi possível copiar o protocolo'))
              }}
            >
              <Copy aria-hidden />
            </Button>
          </dd>
          <dt className="text-muted-foreground">Assunto</dt>
          <dd className="min-w-0 truncate">{subject || '-'}</dd>
        </dl>
      </div>
      <DialogFooter className="flex-row justify-end gap-2 border-t border-border px-5 py-3 sm:space-x-0 sm:px-6">
        <Button variant="outline" onClick={onClose}>
          Fechar
        </Button>
        <Button asChild>
          <a href={url} target="_blank" rel="noreferrer">
            <ExternalLink aria-hidden />
            Ver chamado
          </a>
        </Button>
      </DialogFooter>
    </>
  )
})
